import React, {
  ChangeEvent,
  ReactElement,
  SyntheticEvent,
  useEffect,
  useRef,
} from 'react';
import { useMessage } from '@messageformat/react';
import { Button, Form, FormRow, TextInput } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { IconDelete } from '@op/opds.opux-icons/action';
import styles from './ProductInput.module.scss';
import { getMsgValues } from '../../../utils';

interface Props {
  index: number;
  buyPrice: number;
  sellPrice: number;
  onChange: (index: number, buyPrice: number, sellPrice: number) => void;
  onDelete: (indexToDelete: number) => void;
  titleRef?: React.RefObject<HTMLParagraphElement>;
}

interface FormSectionProps {
  label: string;
  placeholder: string;
  value: number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  cleanNumberString: (amount: number) => string;
}

const FormSection = ({
  label,
  placeholder,
  value,
  onChange,
  cleanNumberString,
}: FormSectionProps): ReactElement => (
  <FormRow label={label} className={styles['form-row']}>
    <TextInput
      amount
      placeholder={placeholder}
      unit="€"
      value={value === 0 ? '' : cleanNumberString(value)}
      onChange={onChange}
    />
  </FormRow>
);

const ProductInput = ({
  index,
  buyPrice,
  sellPrice,
  onChange,
  onDelete,
  titleRef,
}: Props): ReactElement => {
  const productName = useMessage('course9.task1.product');
  const inputText = getMsgValues(useMessage('course9.task1.input_text'));
  const productNumber = index + 1;
  const isNew = useRef(true);

  const cleanNumberString = (amount: number): string => {
    return amount.toString().replace(/^0+(\d+)/, '$1');
  };

  const handleBuyPriceChange = (e: SyntheticEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.currentTarget.value) || 0;
    onChange(index, value, sellPrice);
  };

  const handleSellPriceChange = (e: SyntheticEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.currentTarget.value) || 0;
    onChange(index, buyPrice, value);
  };

  const deleteProduct = (e: SyntheticEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    onDelete(index);
  };

  useEffect(() => {
    if (index === 0) return; // otherwise will steal focus on page load

    if (isNew.current && titleRef?.current) {
      // should only run when element is first added
      titleRef.current.focus();
      isNew.current = false;
    }
  }, [index, titleRef, isNew]);

  return (
    <div
      className={styles['form-container']}
      data-testid={`productInput ${productNumber}`}
      style={{ marginBottom: space.default }}
    >
      <p
        className={styles['product-title']}
        ref={titleRef}
        tabIndex={-1}
      >{`${productName} ${productNumber}`}</p>
      <Form
        stacked
        className={styles['form-layout']}
        onSubmit={(e) => e.preventDefault()}
      >
        <FormSection
          label={inputText[0]}
          placeholder={inputText[2]}
          value={buyPrice}
          onChange={handleBuyPriceChange}
          cleanNumberString={cleanNumberString}
        />
        <FormSection
          label={inputText[1]}
          placeholder={inputText[2]}
          value={sellPrice}
          onChange={handleSellPriceChange}
          cleanNumberString={cleanNumberString}
        />
        {index !== 0 ? (
          <Button
            className={styles['delete-button']}
            minified
            icon={IconDelete}
            iconLabel={inputText[3]}
            onClick={deleteProduct}
            type="button"
          />
        ) : (
          <div style={{ width: '3.8rem' }} />
        )}
      </Form>
    </div>
  );
};

export default ProductInput;
